import { useState } from 'react';
import { showToast } from '../utils/toast';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export const useApi = <T = any>(options: UseApiOptions = {}) => {
  const {
    onSuccess,
    onError,
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
  } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = async (apiCall: () => Promise<T>) => {
    setState({ data: null, loading: true, error: null });

    try {
      const response = await apiCall();
      setState({ data: response, loading: false, error: null });

      if (showSuccessToast && successMessage) {
        showToast.success(successMessage);
      }

      if (onSuccess) {
        onSuccess(response);
      }

      return response;
    } catch (err: any) {
      const error = err?.message || errorMessage || 'An error occurred';
      setState({ data: null, loading: false, error });

      if (showErrorToast) {
        showToast.error(error);
      }

      if (onError) {
        onError(error);
      }

      throw err;
    }
  };

  const reset = () => {
    setState({ data: null, loading: false, error: null });
  };

  return {
    ...state,
    execute,
    reset,
  };
};
